import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Loader2, CreditCard, Settings, HelpCircle, LogOut, Sparkles, ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { GreenLightLogo } from "@/components/GreenLightLogo";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useSubscription } from "@/hooks/useSubscription";
import { useAuth } from "@/hooks/useAuth";
import { SUBSCRIPTION_TIERS, type SubscriptionTier, getLookupKey, type BillingCycle } from "@/lib/subscription";
import { pushPlanSelected, pushBillingCycleToggled } from "@/lib/analytics";
import { BillingToggle } from "@/components/plans/BillingToggle";
import { PlanCards } from "@/components/plans/PlanCards";
import { PricingFAQ } from "@/components/plans/PricingFAQ";

const Plans = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { tier: currentTier, subscribed, isLoading, openCheckout, openCustomerPortal, billingCycle: subBillingCycle } = useSubscription();
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');

  useEffect(() => {
    if (subBillingCycle) {
      setBillingCycle(subBillingCycle);
    }
  }, [subBillingCycle]);

  const getUserInitials = () => {
    if (!user?.email) return '?';
    return user.email.substring(0, 2).toUpperCase();
  };

  const handleContactUsClick = () => {
    if (location.pathname === '/help') {
      const element = document.getElementById('contact-us');
      element?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/help');
      setTimeout(() => {
        const element = document.getElementById('contact-us');
        element?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const handleSelectPlan = (tier: SubscriptionTier) => {
    if (tier === 'free' || (tier === currentTier && !isBillingChanged)) {
      openCustomerPortal();
      return;
    }
    const lookupKey = getLookupKey(tier, billingCycle);
    const config = SUBSCRIPTION_TIERS[tier];
    const displayedPrice = billingCycle === 'monthly' ? config.monthlyPrice : config.annualMonthly;

    pushPlanSelected({
      selected_plan: tier as "starter" | "pro" | "unlimited",
      billing_cycle: billingCycle,
      currency: "USD",
      displayed_price: displayedPrice,
    });

    openCheckout(lookupKey);
  };

  const isBillingChanged = subscribed && currentTier !== 'free' && subBillingCycle && billingCycle !== subBillingCycle;

  const getButtonText = (tier: SubscriptionTier) => {
    if (tier === currentTier && isBillingChanged) return "Change Billing Period";
    if (tier === currentTier) return "Current Plan";
    if (tier === 'free') return "Downgrade";
    const tierOrder = ['free', 'starter', 'pro', 'unlimited'];
    const currentIndex = tierOrder.indexOf(currentTier);
    const targetIndex = tierOrder.indexOf(tier);
    return targetIndex > currentIndex ? "Upgrade" : "Change Plan";
  };

  const isCurrentPlan = (tier: SubscriptionTier) => tier === currentTier;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-foreground/5">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate('/dashboard')} 
              className="flex items-center gap-2"
            >
              <GreenLightLogo width={190} animate={false} />
            </button>

            <div className="flex items-center gap-6">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-1 text-sm font-medium text-foreground/70 hover:text-foreground transition-colors">
                    <HelpCircle className="h-4 w-4" />
                    Help
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/help')}>
                    How to Use
                  </DropdownMenuItem>
                  <DropdownMenuItem className="cursor-pointer" onClick={handleContactUsClick}>
                    Contact Us
                  </DropdownMenuItem>
                  <DropdownMenuItem className="cursor-pointer" onClick={() => window.open('https://form.typeform.com/to/DU5hRCud', '_blank')}>
                    Provide Feedback
                    <ExternalLink className="ml-auto h-3 w-3 text-muted-foreground" />
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="rounded-full focus:outline-none ring-2 ring-accent ring-offset-2">
                    <Avatar className="h-9 w-9 cursor-pointer">
                      <AvatarFallback className="bg-foreground text-background font-medium">
                        {getUserInitials()}
                      </AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>My Account</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/plans')}>
                    <Sparkles className="mr-2 h-4 w-4" />
                    Plans
                    {!subscribed && <Badge variant="secondary" className="ml-auto text-xs">Upgrade</Badge>}
                  </DropdownMenuItem>
                  {subscribed && (
                    <DropdownMenuItem className="cursor-pointer" onClick={openCustomerPortal}>
                      <CreditCard className="mr-2 h-4 w-4" />
                      Billing
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/settings')}>
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    className="cursor-pointer text-destructive focus:text-destructive"
                    onClick={handleSignOut}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main>
        <div className="container mx-auto px-6 pt-12 pb-4">
          <div className="text-center mb-6">
            <h1 className="font-display text-4xl font-bold text-foreground mb-4">
              Choose Your Plan
            </h1>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-8">
              Start reviewing your next email in minutes. No setup required.
            </p>

            <BillingToggle initialValue={billingCycle} onChange={setBillingCycle} />
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-accent" />
          </div>
        ) : (
          <>
            <PlanCards
              billing={billingCycle}
              currentTier={currentTier}
              onSelectPlan={handleSelectPlan}
              getButtonText={getButtonText}
              isCurrentPlan={isCurrentPlan}
              subscribed={subscribed}
            />

            <div className="text-center py-8">
              <p className="text-muted-foreground">
                All plans include unlimited reviewers and email forwarding.{" "}
                <button 
                  onClick={() => navigate('/dashboard')}
                  className="text-link hover:text-link-hover hover:underline"
                >
                  Back to Dashboard
                </button>
              </p>
            </div>

            <PricingFAQ />
          </>
        )}
      </main>
    </div>
  );
};

export default Plans;
